import api, { ApiResponse } from './api';

export interface PackageStats {
  incoming: number;
  outgoing: number;
  escorts: number;
}

export interface MonthlyStats {
  month: string;
  incoming: number;
  outgoing: number;
  escorts: number;
}

export interface TenantActivity {
  id: number;
  building: string;
  reason: string;
  type: number;
  tenant_name: string;
  tenant_contact_name: string;
  status: string;
  created_at: string;
}

export interface VolumeReportResult {
  totals: PackageStats;
  series: Array<{
    period: string;
    incoming: number;
    outgoing: number;
    escorts: number;
  }>;
  activity: TenantActivity[];
}

export interface Package {
  id: number;
  tracking_number: string;
  carrier_id: number;
  tenant_id: number;
  tenant_contact_id: number;
  building_id: number;
  status: number;
  type: number;
  notes: string;
  created_at: string;
  updated_at: string;
}

export const packageService = {
  async getVolumeReport(
    buildingId: number,
    startDate: string,
    endDate: string,
  ): Promise<VolumeReportResult> {
    const response = await api.get<ApiResponse<VolumeReportResult>>(
      `/buildings/${buildingId}/reports/volume`,
      {
        params: {
          start_date: startDate,
          end_date: endDate,
          granularity: 'auto',
          activity_limit: 10,
        },
      },
    );

    if (response.status === 403) {
      throw new Error('You do not have access to reports for this building.');
    }
    if (response.data.CODE === 200) return response.data.RESULT;
    throw new Error(response.data.MESSAGE || 'Failed to fetch volume report');
  },

  async getStats(buildingId?: number, startDate?: string, endDate?: string): Promise<PackageStats> {
    const params = new URLSearchParams();
    if (buildingId) params.append('building_id', buildingId.toString());
    if (startDate) params.append('start_date', startDate);
    if (endDate) params.append('end_date', endDate);

    const response = await api.get<ApiResponse<PackageStats>>(`/package-stats?${params}`);

    if (response.data.CODE === 200) {
      return response.data.RESULT;
    }

    throw new Error(response.data.MESSAGE || 'Failed to fetch stats');
  },

  async getMonthlyStats(buildingId?: number, startDate?: string, endDate?: string): Promise<MonthlyStats[]> {
    const params = new URLSearchParams();
    if (buildingId) params.append('building_id', buildingId.toString());
    if (startDate) params.append('start_date', startDate);
    if (endDate) params.append('end_date', endDate);

    const response = await api.get<ApiResponse<MonthlyStats[]>>(`/package-monthly-stats?${params}`);

    if (response.data.CODE === 200) {
      return response.data.RESULT;
    }

    throw new Error(response.data.MESSAGE || 'Failed to fetch monthly stats');
  },

  async getTenantActivity(buildingId?: number, limit = 10, startDate?: string, endDate?: string): Promise<TenantActivity[]> {
    const params = new URLSearchParams();
    if (buildingId) params.append('building_id', buildingId.toString());
    params.append('limit', limit.toString());
    if (startDate) params.append('start_date', startDate);
    if (endDate) params.append('end_date', endDate);

    const response = await api.get<ApiResponse<TenantActivity[]>>(`/tenant-activity?${params}`);

    if (response.data.CODE === 200) {
      return response.data.RESULT;
    }

    throw new Error(response.data.MESSAGE || 'Failed to fetch tenant activity');
  },

  async getPackages(params: {
    building_id?: number;
    status?: number;
    page?: number;
    per_page?: number;
  }): Promise<{ data: Package[]; total: number }> {
    const searchParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined) searchParams.append(key, value.toString());
    });

    const response = await api.get<ApiResponse<{ data: Package[]; total: number }>>(`/manage-package?${searchParams}`);

    if (response.data.CODE === 200) {
      return response.data.RESULT;
    }

    throw new Error(response.data.MESSAGE || 'Failed to fetch packages');
  },
};

export default packageService;
